/**
 * Iconos de línea de la Práctica de Escenarios (24-grid, stroke currentColor).
 * Portados de claude-design/orborix-speak/v4/project/scenario-data.jsx — son
 * primitivos tipados, sin `Object.assign(window, ...)`.
 */

import type { ReactElement } from 'react';

interface IconProps {
  size?: number;
}

const stroke = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function IcWork({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.8} {...stroke}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7 V 5.5 a 2 2 0 0 1 2 -2 h 4 a 2 2 0 0 1 2 2 V 7" />
      <path d="M3 12 h 18" />
    </svg>
  );
}

export function IcTravel({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.8} {...stroke}>
      <path d="M10 4 a 1.5 1.5 0 0 1 3 0 l 0 6 7 4 v 2 l -7 -2 -0.5 4 2 1.5 V 21 l -3.5 -1 -3.5 1 v -1.5 l 2 -1.5 L 9 14 2 16 v -2 l 7 -4 Z" />
    </svg>
  );
}

export function IcSocial({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.8} {...stroke}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19 c 0.6 -3.2, 3 -5, 5.5 -5 s 4.9 1.8, 5.5 5" />
      <path d="M16 5.5 a 3 3 0 0 1 0 5.6" />
      <path d="M17.5 14 c 2 0.5, 3.3 2.2, 3.7 4.4" />
    </svg>
  );
}

export function IcDaily({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.8} {...stroke}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7 v 5 l 3.2 2" />
    </svg>
  );
}

export function IcSpark({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.9} {...stroke}>
      <path d="M12 3 v 5 M 12 16 v 5 M 3 12 h 5 M 16 12 h 5" />
    </svg>
  );
}

export function IcPlus({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2.2} {...stroke}>
      <path d="M12 5 V 19 M 5 12 H 19" />
    </svg>
  );
}

export function IcCheck({ size = 13 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2.8} {...stroke}>
      <path d="M4 12 L 10 18 L 20 6" />
    </svg>
  );
}

export function IcChevron({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2.2} {...stroke}>
      <path d="M6 9 L 12 15 L 18 9" />
    </svg>
  );
}

export function IcBack({ size = 20 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2} {...stroke}>
      <path d="M15 6 L 9 12 L 15 18" />
    </svg>
  );
}

/** `IcBack` espejado: el empujón hacia la derecha del rail de "Mis escenas". */
export function IcForward({ size = 20 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2} {...stroke}>
      <path d="M9 6 L 15 12 L 9 18" />
    </svg>
  );
}

export function IcArrowR({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2} {...stroke}>
      <path d="M5 12 H 19 M 13 6 L 19 12 L 13 18" />
    </svg>
  );
}

export function IcClose({ size = 18 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2} fill="none" stroke="currentColor" strokeLinecap="round">
      <path d="M6 6 L 18 18 M 18 6 L 6 18" />
    </svg>
  );
}

export function IcReplay({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.9} {...stroke}>
      <path d="M4 12 a 8 8 0 1 0 2.5 -5.8" />
      <path d="M4 4 v 4 h 4" />
    </svg>
  );
}

export function IcTarget({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.8} {...stroke}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IcMask({ size = 15 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={1.7} {...stroke}>
      <path d="M4 6.5 c 5 -1.4, 11 -1.4, 16 0 v 4.5 a 3.6 3.6 0 0 1 -3.2 3.6 c -1.3 1.8 -2 2.9 -4.8 2.9 s -3.5 -1.1 -4.8 -2.9 A 3.6 3.6 0 0 1 4 11 Z" />
    </svg>
  );
}

export function IcClock({ size = 12 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2} {...stroke}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7 v 5 l 3 2" />
    </svg>
  );
}

export function IcStarF({ size = 16 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor">
      <path d="M12 3 L 14.4 9.4 L 21 10 L 16 14.4 L 17.6 21 L 12 17.4 L 6.4 21 L 8 14.4 L 3 10 L 9.6 9.4 Z" />
    </svg>
  );
}

export function IcWarning({ size = 26 }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} strokeWidth={2} {...stroke}>
      <path d="M12 8 v 5" />
      <circle cx="12" cy="16.5" r="0.6" fill="currentColor" />
      <path d="M10.3 3.8 L 2.5 18 a 2 2 0 0 0 1.7 3 h 15.6 a 2 2 0 0 0 1.7 -3 L 13.7 3.8 a 2 2 0 0 0 -3.4 0 Z" />
    </svg>
  );
}

/** Mapea la categoría del catálogo a su icono. Fallback a Spark. */
export function categoryIcon(categoryId: string): (p: IconProps) => ReactElement {
  switch (categoryId) {
    case 'work':
      return IcWork;
    case 'travel':
      return IcTravel;
    case 'social':
      return IcSocial;
    case 'daily':
      return IcDaily;
    case 'emergency':
      return IcWarning;
    default:
      return IcSpark;
  }
}
